import { useRef, useState } from "react";
import { ArrowRight, Loader2, Play } from "lucide-react";

import { cn } from "@/lib/utils";

interface ProfilePreviewCardProps {
  /** Display name. */
  name: string;
  /** Optional secondary line — typically `${role} · ${company}` or email fallback. */
  subtitle?: string | null;
  /** Signed video URL. May be undefined while loading. */
  videoUrl?: string;
  /** Highlighted "Looking for" copy. Rendered in the amber callout. */
  lookingFor?: string | null;
  /** Plain-toned "About" copy. */
  about?: string | null;
  /** Plain-toned "Can help with" copy (additional context, optional). */
  canHelpWith?: string | null;
  /** Edit handler — secondary button. */
  onEdit: () => void;
  /** Continue handler — primary button. Wires the user into the live feed. */
  onContinue: () => void;
  /** Footer copy override on the primary button. */
  continueLabel?: string;
}

/**
 * Preview card matching the v6.05 "how others see you" mock — large circular
 * pitch video with a blue outline ring, a floating play button with a pulsing
 * halo, a duration chip pinned to the corner, the "this is how you appear"
 * pill, and the standard amber/plain info blocks under the identity row.
 *
 * The component owns its own video element so we can paint the prototype's
 * floating play button without fighting the centred overlay that the shared
 * `CirclePlayer` ships with.
 */
export function ProfilePreviewCard({
  name,
  subtitle,
  videoUrl,
  lookingFor,
  about,
  canHelpWith,
  onEdit,
  onContinue,
  continueLabel = "Looks good — Browse feed",
}: ProfilePreviewCardProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [duration, setDuration] = useState<number | null>(null);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v || !ready) return;
    if (v.paused) {
      void v.play();
    } else {
      v.pause();
    }
  };

  return (
    <div className="rounded-[20px] border border-[#e2e8f0] bg-white shadow-[0_2px_16px_rgba(0,0,0,0.06)]">
      {/* Video area */}
      <div className="flex flex-col items-center px-5 pt-7">
        <div className="relative mb-11 h-[210px] w-[210px]">
          {/* Outer ring composition: blue outline (3px) → white border (3px) → video.
              We use stacked box-shadows so the rings respect the 50% radius and the
              soft drop shadow stays underneath them. */}
          <button
            type="button"
            onClick={togglePlay}
            onContextMenu={(e) => e.preventDefault()}
            disabled={!videoUrl}
            aria-label={
              !videoUrl
                ? "Loading pitch"
                : !ready
                  ? "Buffering pitch"
                  : playing
                    ? "Pause pitch"
                    : "Play pitch"
            }
            className={cn(
              "relative block h-[210px] w-[210px] overflow-hidden rounded-full bg-black",
              "shadow-[0_0_0_3px_white,0_0_0_6px_#2563eb,0_4px_24px_rgba(37,99,235,0.18),0_2px_8px_rgba(0,0,0,0.08)]",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563eb] focus-visible:ring-offset-[6px]",
            )}
          >
            {videoUrl ? (
              <video
                ref={videoRef}
                // `#t=0.1` is a media fragment URI — it never goes to the
                // server, it just tells the browser to seek 100 ms in so it
                // decodes and paints a real first frame instead of black.
                src={videoUrl.includes("#t=") ? videoUrl : `${videoUrl}#t=0.1`}
                playsInline
                preload="metadata"
                muted
                disablePictureInPicture
                controlsList="nodownload nofullscreen noremoteplayback"
                onLoadedMetadata={(e) => {
                  const v = e.currentTarget;
                  const d = v.duration;
                  if (Number.isFinite(d) && d > 0) setDuration(d);
                  // Belt-and-braces seek for browsers that ignore the URL
                  // fragment (some Chrome builds). Forces frame decode.
                  try {
                    if (v.currentTime < 0.05) v.currentTime = 0.1;
                  } catch {
                    /* metadata-only streams may reject seek — ignore. */
                  }
                  setReady(true);
                }}
                onLoadedData={() => setReady(true)}
                onSeeked={() => setReady(true)}
                onPlay={(e) => {
                  // Element starts muted so iOS Safari is willing to paint
                  // the poster frame; unmute on the user-initiated play.
                  e.currentTarget.muted = false;
                  setPlaying(true);
                }}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                onWaiting={() => setReady(false)}
                onPlaying={() => setReady(true)}
                className="block h-full w-full object-cover"
              />
            ) : (
              <div className="grid h-full w-full place-items-center bg-gradient-to-br from-[#e2e8f0] to-[#cbd5e1] text-white/70">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            )}
          </button>

          {/* Floating play button — only visible while paused. Pulsing halo
              uses the built-in `animate-ping` with a slowed duration so the
              motion stays calm. */}
          {videoUrl && !playing ? (
            <button
              type="button"
              onClick={togglePlay}
              disabled={!ready}
              aria-hidden="true"
              tabIndex={-1}
              className={cn(
                "absolute -bottom-[22px] left-1/2 z-10 grid h-[52px] w-[52px] -translate-x-1/2 place-items-center",
                "rounded-full border-[3px] border-white bg-[#2563eb] shadow-[0_4px_16px_rgba(37,99,235,0.4)]",
                "transition hover:bg-[#1d4ed8] disabled:opacity-80",
              )}
            >
              {ready ? (
                <Play
                  className="ml-0.5 h-4 w-4 fill-white text-white"
                  strokeWidth={0}
                />
              ) : (
                <Loader2 className="h-4 w-4 animate-spin text-white" />
              )}
              <span
                className="pointer-events-none absolute -inset-[6px] rounded-full border-2 border-[#2563eb]/30 motion-safe:animate-ping"
                style={{ animationDuration: "2.2s" }}
              />
            </button>
          ) : null}

          {/* Duration chip — bottom-right corner of the circle. */}
          {duration != null ? (
            <span className="absolute -bottom-[14px] right-2 rounded-[6px] border border-[#e2e8f0] bg-[#f8fafc] px-2 py-[3px] text-[11px] font-bold text-[#475569]">
              {formatDuration(duration)}
            </span>
          ) : null}
        </div>

        {/* Preview pill */}
        <div className="mb-5 inline-flex items-center gap-[7px] rounded-full border border-[#e2e8f0] bg-[#f8fafc] px-[14px] py-[7px] text-[12px] font-semibold text-[#6c757d]">
          <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#94a3b8]" />
          This is how you appear in others' feeds
        </div>
      </div>

      {/* Body */}
      <div className="px-5">
        <p className="text-[19px] font-bold tracking-[-0.4px] text-[#1a1a1a]">
          {name}
        </p>
        {subtitle ? (
          <p className="mt-[3px] text-[13px] text-[#6c757d]">{subtitle}</p>
        ) : null}

        <div className="mt-4 flex flex-col gap-2.5">
          {lookingFor ? (
            <InfoBox tone="amber" label="Looking for" text={lookingFor} />
          ) : null}
          {about ? <InfoBox tone="plain" label="About" text={about} /> : null}
          {canHelpWith ? (
            <InfoBox tone="plain" label="Can help with" text={canHelpWith} />
          ) : null}
        </div>
      </div>

      {/* Footer */}
      <div className="flex gap-2.5 px-5 py-5">
        <button
          type="button"
          onClick={onEdit}
          className="h-[52px] rounded-[12px] border-[1.5px] border-[#e2e8f0] bg-[#f8fafc] px-5 text-[15px] font-bold text-[#1a1a1a] transition hover:border-[#cbd5e1] hover:bg-white"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={onContinue}
          className="flex h-[52px] flex-1 items-center justify-center gap-2 rounded-[12px] bg-[#2563eb] text-[15px] font-bold tracking-[-0.1px] text-white transition hover:bg-[#1d4ed8]"
        >
          {continueLabel}
          <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

interface InfoBoxProps {
  tone: "amber" | "plain";
  label: string;
  text: string;
}

function InfoBox({ tone, label, text }: InfoBoxProps) {
  if (tone === "amber") {
    return (
      <div className="rounded-r-[6px] border-l-[3px] border-[#f59e0b] bg-[#fffbeb] px-3 py-2.5">
        <p className="text-[10px] font-bold uppercase tracking-[0.5px] text-[#92400e]">
          {label}
        </p>
        <p className="mt-1 whitespace-pre-line text-[13px] leading-[1.5] text-[#78350f]">
          {text}
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-[8px] border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5">
      <p className="text-[10px] font-bold uppercase tracking-[0.5px] text-[#94a3b8]">
        {label}
      </p>
      <p className="mt-1 whitespace-pre-line text-[13px] leading-[1.5] text-[#1a1a1a]">
        {text}
      </p>
    </div>
  );
}

function formatDuration(seconds: number): string {
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
